import { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import i18n from '../i18n';

const LANG_CODES = ['en', 'pt'];

const LANG_KEY_PREF = 'user-language-preference';
const LANG_KEY_SESSION = 'user-language-session';

export default function useLanguage() {
  const userPreferences = useSelector(
    (state) => state.preferences?.preferences
  );

  const getInitialLangCode = useCallback(() => {
    const sessionLang = localStorage.getItem(LANG_KEY_SESSION);
    const savedPrefLang = localStorage.getItem(LANG_KEY_PREF);
    const userPrefLang = userPreferences?.language;

    return (
      sessionLang ||
      userPrefLang ||
      savedPrefLang ||
      i18n.language ||
      navigator.language.slice(0, 2) ||
      'en'
    );
  }, [userPreferences]);

  const [language, setLangState] = useState(getInitialLangCode());

  // Sync when i18n language changes externally (e.g. from another component)
  useEffect(() => {
    const onLangChanged = (lng) => {
      const code = (lng || '').slice(0, 2);
      if (LANG_CODES.includes(code) && code !== language) {
        setLangState(code);
      }
    };
    i18n.on('languageChanged', onLangChanged);
    return () => i18n.off('languageChanged', onLangChanged);
  }, [language]);

  useEffect(() => {
    const langCode = getInitialLangCode();
    if (langCode !== language || langCode !== i18n.language) {
      if (langCode !== i18n.language) {
        i18n.changeLanguage(langCode);
      }
      if (langCode !== language) {
        setLangState(langCode);
      }
    }
  }, [userPreferences, language, getInitialLangCode]);

  const setLanguage = (code) => {
    if (!LANG_CODES.includes(code)) return;
    if (code === language && code === i18n.language) return;
    localStorage.setItem(LANG_KEY_SESSION, code);
    if (code !== i18n.language) i18n.changeLanguage(code);
    if (code !== language) setLangState(code);
  };

  return {
    language,
    setLanguage,
  };
}
